import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Slider,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material'
import {
    PlayArrow,
    Pause,
    SkipPrevious,
    SkipNext,
    FirstPage,
    LastPage,
    Speed,
    Opacity,
} from '@mui/icons-material'
import i18n from 'simple-react-i18n'
import { MAIN_RADIUS } from 'utils/constants/Theme'
import {
    MODEL_AROME,
    DEFAULT_ANIMATION_SPEED_MS,
    PRELOAD_WINDOW,
    SPEED_OPTIONS,
    getRadarModelConfig,
    PRECIP_COLORS,
    fetchRuns,
    fetchMapFrames,
    getFullImageUrl,
    parseRunMoment,
    formatSimulationDate,
    formatForecastDate,
} from './RadarService'
import RadarLayer, { processRadarFluxImage } from '../../../utils/mapUtils/layers/RadarLayer'

const RadarOverlay = ({ model = MODEL_AROME }) => {
    const intervalRef = useRef(null)
    const radarLayerRef = useRef(null)

    const [activeModel, setActiveModel] = useState(model || MODEL_AROME)
    const config = getRadarModelConfig(activeModel)

    const [availableRuns, setAvailableRuns] = useState([])
    const [selectedRun, setSelectedRun] = useState(null)
    const [mapFrames, setMapFrames] = useState([])
    const [currentIndex, setCurrentIndex] = useState(0)

    const [isPlaying, setIsPlaying] = useState(false)
    const [playbackSpeed, setPlaybackSpeed] = useState(1)
    const [layerOpacity, setLayerOpacity] = useState(0.75)

    const currentFrame = useMemo(() => (
        mapFrames && mapFrames.length > 0 && mapFrames[currentIndex] ? mapFrames[currentIndex] : null
    ), [mapFrames, currentIndex])

    const currentForecastHour = useMemo(() => (
        currentFrame?.forecastHour ?? (currentIndex + 1)
    ), [currentFrame, currentIndex])

    useEffect(() => {
        if (model && model !== activeModel) {
            setActiveModel(model)
        }
    }, [model, activeModel])

    useEffect(() => {
        const checkMapAndAttach = () => {
            const olMap = window.sieauOlMap
            if (!olMap) return false

            if (!radarLayerRef.current) {
                radarLayerRef.current = RadarLayer({
                    model: activeModel,
                    opacity: layerOpacity,
                    visible: true,
                })
                olMap.addLayer(radarLayerRef.current.getLayer())
                if (currentFrame?.filePath) {
                    radarLayerRef.current.updateUrl(getFullImageUrl(currentFrame.filePath))
                }
            } else {
                radarLayerRef.current.setModel(activeModel)
                radarLayerRef.current.setOpacity(layerOpacity)
            }
            return true
        }

        const attached = checkMapAndAttach()
        const retryTimer = attached ? null : setInterval(checkMapAndAttach, 400)

        return () => {
            if (retryTimer) clearInterval(retryTimer)
            if (radarLayerRef.current && window.sieauOlMap) {
                window.sieauOlMap.removeLayer(radarLayerRef.current.getLayer())
                radarLayerRef.current = null
            }
        }
    }, [activeModel, layerOpacity, currentFrame])

    useEffect(() => {
        const isMounted = { current: true }
        fetchRuns(activeModel).then((runs) => {
            if (!isMounted.current) return
            setAvailableRuns(runs)
            if (runs && runs.length > 0) {
                setSelectedRun(runs[runs.length - 1])
            }
        })
        return () => {
            isMounted.current = false
        }
    }, [activeModel])

    useEffect(() => {
        const isMounted = { current: true }
        fetchMapFrames(activeModel, selectedRun).then((frames) => {
            if (!isMounted.current) return
            setMapFrames(frames)
            setCurrentIndex(0)
        })
        return () => {
            isMounted.current = false
        }
    }, [activeModel, selectedRun])

    useEffect(() => {
        if (!currentFrame?.filePath || !radarLayerRef.current) return
        const fullUrl = getFullImageUrl(currentFrame.filePath)
        processRadarFluxImage(fullUrl)
        radarLayerRef.current.updateUrl(fullUrl)
    }, [currentFrame])

    useEffect(() => {
        if (!mapFrames || mapFrames.length === 0) return
        const startIndex = currentIndex + 1
        const endIndex = Math.min(startIndex + PRELOAD_WINDOW, mapFrames.length)
        mapFrames.slice(startIndex, endIndex).forEach((frame) => {
            if (frame?.filePath) {
                processRadarFluxImage(getFullImageUrl(frame.filePath))
            }
        })
    }, [mapFrames, currentIndex])

    const handleNextFrame = useCallback(() => {
        setCurrentIndex((prev) => {
            if (mapFrames.length === 0) return 0
            if (prev >= mapFrames.length - 1) return 0
            return prev + 1
        })
    }, [mapFrames])

    const handlePrevFrame = useCallback(() => {
        setCurrentIndex((prev) => {
            if (mapFrames.length === 0) return 0
            if (prev <= 0) return mapFrames.length - 1
            return prev - 1
        })
    }, [mapFrames])

    const startAnimation = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current)
        const frameIntervalMs = Math.max(120, Math.round(DEFAULT_ANIMATION_SPEED_MS / playbackSpeed))
        intervalRef.current = setInterval(handleNextFrame, frameIntervalMs)
        setIsPlaying(true)
    }, [playbackSpeed, handleNextFrame])

    const stopAnimation = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
        setIsPlaying(false)
    }, [])

    const togglePlay = useCallback(() => {
        if (isPlaying) {
            stopAnimation()
        } else {
            startAnimation()
        }
    }, [isPlaying, startAnimation, stopAnimation])

    useEffect(() => {
        if (isPlaying) {
            startAnimation()
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [isPlaying, playbackSpeed, startAnimation])

    const handleSliderChange = useCallback((_event, newValue) => {
        stopAnimation()
        if (newValue >= 0 && newValue < mapFrames.length) {
            setCurrentIndex(newValue)
        }
    }, [mapFrames, stopAnimation])

    const handleSpeedChange = useCallback((event) => {
        setPlaybackSpeed(event.target.value)
    }, [])

    const handleOpacityChange = useCallback((_event, newValue) => {
        setLayerOpacity(newValue)
    }, [])

    const goToFirst = useCallback(() => {
        stopAnimation()
        setCurrentIndex(0)
    }, [stopAnimation])

    const goToLast = useCallback(() => {
        stopAnimation()
        if (mapFrames.length > 0) {
            setCurrentIndex(mapFrames.length - 1)
        }
    }, [mapFrames, stopAnimation])

    const goToPrev = useCallback(() => {
        stopAnimation()
        handlePrevFrame()
    }, [stopAnimation, handlePrevFrame])

    const goToNext = useCallback(() => {
        stopAnimation()
        handleNextFrame()
    }, [stopAnimation, handleNextFrame])

    const currentRunMoment = useMemo(() => parseRunMoment(selectedRun), [selectedRun])
    const isFirstFrame = currentIndex <= 0
    const isLastFrame = currentIndex >= mapFrames.length - 1

    return (
        <>
            <Grid
                container
                alignItems='center'
                wrap='nowrap'
                sx={{
                    position: 'fixed',
                    bottom: 104,
                    right: 20,
                    zIndex: 1100,
                    backgroundColor: 'background.paper',
                    px: 1.2,
                    py: 0.5,
                    borderRadius: MAIN_RADIUS,
                    boxShadow: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography variant='caption' sx={{ fontWeight: 700, mr: 1, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                    {`${i18n.radarUnitMmh} :`}
                </Typography>
                <Grid container wrap='nowrap'>
                    {PRECIP_COLORS.map((colorItem, colorIndex) => (
                        <Stack
                            key={colorItem.label}
                            alignItems='center'
                            sx={{ minWidth: { xs: 14, sm: 20 } }}
                        >
                            <Grid
                                size={12}
                                sx={{
                                    height: 12,
                                    backgroundColor: colorItem.color,
                                    borderRight: colorIndex < PRECIP_COLORS.length - 1 ? '1px solid' : 'none',
                                    borderColor: 'divider',
                                }}
                            />
                            <Typography
                                variant='caption'
                                sx={{
                                    fontSize: '0.65rem',
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                    mt: 0.2,
                                    fontWeight: 600,
                                    lineHeight: 1,
                                }}
                            >
                                {colorItem.label}
                            </Typography>
                        </Stack>
                    ))}
                </Grid>
            </Grid>

            <Grid
                container
                justifyContent='center'
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    left: 0,
                    right: 0,
                    zIndex: 1100,
                    px: 2,
                }}
            >
                <Grid
                    container
                    size={10}
                    sx={{
                        backgroundColor: 'background.paper',
                        py: 1,
                        px: { xs: 1.5, md: 2 },
                        borderRadius: MAIN_RADIUS,
                        boxShadow: 4,
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Grid
                        container
                        alignItems='center'
                        justifyContent='space-between'
                        spacing={1}
                        sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}
                    >
                        <Grid container alignItems='center' spacing={2}>
                            <Typography variant='subtitle2' sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                                {`${i18n.radarSimulation} : ${formatSimulationDate(currentRunMoment)}`}
                            </Typography>
                            <Typography variant='body2' sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                                {`${i18n.radarForecast} : ${formatForecastDate(currentRunMoment, currentForecastHour)} (H+${currentForecastHour})`}
                            </Typography>
                        </Grid>

                        <Grid container alignItems='center' spacing={0.5}>
                            <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', mr: 0.5 }}>
                                {`${i18n.radarRuns} :`}
                            </Typography>
                            {availableRuns.slice(-8).map((runStr) => {
                                const isSelected = selectedRun === runStr || (!selectedRun && runStr === availableRuns[availableRuns.length - 1])
                                const runMomentDate = parseRunMoment(runStr)
                                const runLabel = `${runMomentDate.format('DD/MM')} ${runMomentDate.format('HH')}z`
                                return (
                                    <Button
                                        key={runStr}
                                        size='small'
                                        variant={isSelected ? 'contained' : 'outlined'}
                                        color={isSelected ? 'primary' : 'inherit'}
                                        onClick={() => {
                                            stopAnimation()
                                            setSelectedRun(runStr)
                                        }}
                                        sx={{
                                            py: 0.1,
                                            px: 0.7,
                                            fontSize: '0.68rem',
                                            fontWeight: 700,
                                            borderRadius: MAIN_RADIUS,
                                        }}
                                    >
                                        {runLabel}
                                    </Button>
                                )
                            })}
                        </Grid>
                    </Grid>

                    <Grid container wrap='nowrap' size={12} alignItems='center' spacing={1}>
                        <Grid>
                            <Tooltip title={i18n.radarGoToBeginning}>
                                <IconButton onClick={goToFirst} disabled={isFirstFrame} size='small'>
                                    <FirstPage fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        <Grid>
                            <Tooltip title={i18n.radarPreviousOneHour}>
                                <IconButton onClick={goToPrev} disabled={isFirstFrame} size='small'>
                                    <SkipPrevious fontSize='medium' />
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        <Grid>
                            <Tooltip title={isPlaying ? i18n.radarPause : i18n.radarPlay}>
                                <IconButton
                                    onClick={togglePlay}
                                    sx={{
                                        backgroundColor: isPlaying ? 'warning.main !important' : 'primary.main !important',
                                        color: 'primary.contrastText !important',
                                        p: 1,
                                        '&:hover': {
                                            backgroundColor: isPlaying ? 'warning.dark !important' : 'primary.dark !important',
                                        },
                                    }}
                                >
                                    {isPlaying ? <Pause fontSize='small' /> : <PlayArrow fontSize='small' />}
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        <Grid>
                            <Tooltip title={i18n.radarNextOneHour}>
                                <IconButton onClick={goToNext} disabled={isLastFrame} size='small'>
                                    <SkipNext fontSize='medium' />
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        <Grid>
                            <Tooltip title={i18n.radarGoToEnd}>
                                <IconButton onClick={goToLast} disabled={isLastFrame} size='small'>
                                    <LastPage fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        <Grid size='grow' sx={{ minWidth: 120, mx: { xs: 1, sm: 2 } }}>
                            <Slider
                                value={currentIndex}
                                min={0}
                                max={Math.max(0, mapFrames.length - 1)}
                                step={1}
                                onChange={handleSliderChange}
                                aria-label={i18n.radarForecast}
                                sx={{
                                    height: 6,
                                    '& .MuiSlider-thumb': {
                                        width: 14,
                                        height: 14,
                                        border: '2px solid',
                                        borderColor: 'background.paper',
                                    },
                                }}
                            />
                        </Grid>

                        <Grid container alignItems='center' spacing={0.3} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                            <Tooltip title={i18n.radarPlaybackSpeed}>
                                <Speed fontSize='small' sx={{ color: 'text.secondary' }} />
                            </Tooltip>
                            <Select
                                value={playbackSpeed}
                                onChange={handleSpeedChange}
                                size='small'
                                variant='standard'
                                disableUnderline
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    px: 0.4,
                                    py: 0.2,
                                    borderRadius: MAIN_RADIUS,
                                    '& .MuiSelect-icon': { color: 'text.secondary' },
                                }}
                            >
                                {SPEED_OPTIONS.map((spd) => (
                                    <MenuItem key={spd} value={spd} sx={{ fontSize: '0.75rem' }}>
                                        {`${spd}x`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>

                        <Grid container alignItems='center' spacing={0.8} sx={{ display: { xs: 'none', md: 'flex' } }}>
                            <Tooltip title={i18n.radarLayerOpacity}>
                                <Opacity fontSize='small' sx={{ color: 'text.secondary' }} />
                            </Tooltip>
                            <Slider
                                value={layerOpacity}
                                min={0.1}
                                max={1}
                                step={0.05}
                                onChange={handleOpacityChange}
                                size='small'
                                aria-label={i18n.radarLayerOpacity}
                                sx={{
                                    height: 4,
                                    minWidth: 80,
                                    '& .MuiSlider-thumb': { width: 10, height: 10 },
                                }}
                            />
                        </Grid>

                        <Grid>
                            <Typography
                                variant='caption'
                                sx={{
                                    fontWeight: 800,
                                    fontSize: '0.78rem',
                                    minWidth: 70,
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {`H+${currentForecastHour} / H+${mapFrames.length > 0 ? (mapFrames[mapFrames.length - 1]?.forecastHour || mapFrames.length) : config.maxEch}`}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

RadarOverlay.propTypes = {
    model: PropTypes.string,
}

export default RadarOverlay
